import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { patterns, sitemap } from '../util/url-utils';
import { RequireAuth } from '../restricted/components/RequireAuth';
import { RequirePermission } from '../restricted/components/RequirePermission';
import ErrorBoundary from 'app/shared/error/error-boundary-catcher';
import { FeatureErrorFallback } from 'app/shared/error/FeatureErrorFallback';
import { LoadingSkeleton } from 'app/modules/loading-lib/component/LoadingSkeleton';

// ─── Infrastructure (eager) ───────────────────────────────────────────────────
import { LandingPage } from 'app/views/landing-page/LandingPage';
import Logout from 'app/views/logout/logout';
import { LoginRedirect } from 'app/views/login/login-redirect';
import { ErrorPage } from 'app/views/error-page';
import PageNotFound from 'app/views/not-found/page-not-found';
import { Welcome } from 'app/views/welcome';
import { AcceptInvitation } from 'app/views/invitations/AcceptInvitation';

const NewsletterVisualEditor = React.lazy(() => import('app/views/newsletters-page/components/visualEditor'));

// ─── Feature views (lazy) ─────────────────────────────────────────────────────
const ModuleRoot = React.lazy(() => import('app/views/module-root').then(m => ({ default: m.ModuleRoot })));
const AccountPage = React.lazy(() => import('app/views/account-page').then(m => ({ default: m.AccountPage })));
const UserProfile = React.lazy(() => import('app/views/profile-page').then(m => ({ default: m.UserProfile })));
const Applications = React.lazy(() => import('app/views/applications-page').then(m => ({ default: m.Applications })));

// Newsletters
const NewsLettersPage = React.lazy(() =>
  import('app/views/newsletters-page/newsletters').then(m => ({ default: m.NewsLettersPage })),
);
const NewsLetterInstances = React.lazy(() =>
  import('app/views/newsletters-page/instances').then(m => ({ default: m.NewsLetterInstances })),
);
const NewInboundNewsLetter = React.lazy(() =>
  import('app/views/newsletters-page/new-inbound-newsletter').then(m => ({ default: m.NewInboundNewsLetter })),
);
const NewOutboundNewsLetter = React.lazy(() =>
  import('app/views/newsletters-page/new-outbound-newsletter').then(m => ({ default: m.NewOutboundNewsLetter })),
);
const EditInboundNewsLetter = React.lazy(() =>
  import('app/views/newsletters-page/edit-inbound-newsletter').then(m => ({ default: m.EditInboundNewsLetter })),
);
const EditOutboundNewsLetter = React.lazy(() =>
  import('app/views/newsletters-page/edit-outbound-newsletter').then(m => ({ default: m.EditOutboundNewsLetter })),
);
const ValidateBlocksPage = React.lazy(() =>
  import('app/views/newsletters-page/validate-blocks').then(m => ({ default: m.ValidateBlocksPage })),
);

// Data Manager
const Sources = React.lazy(() => import('app/views/data-manager/sources').then(m => ({ default: m.Sources })));
const SourceManagement = React.lazy(() =>
  import('app/views/data-manager/documents').then(m => ({ default: m.SourceManagement })),
);
const Collections = React.lazy(() =>
  import('app/views/data-manager/collections').then(m => ({ default: m.Collections })),
);
const NewCollection = React.lazy(() =>
  import('app/views/data-manager/collections/create').then(m => ({ default: m.NewCollection })),
);
const CollectionDetail = React.lazy(() =>
  import('app/views/data-manager/collections/detail').then(m => ({ default: m.CollectionDetail })),
);
const EditCollection = React.lazy(() =>
  import('app/views/data-manager/collections/edit').then(m => ({ default: m.EditCollection })),
);
const Pipelines = React.lazy(() => import('app/views/data-manager/pipelines').then(m => ({ default: m.Pipelines })));
const NewPipeline = React.lazy(() =>
  import('app/views/data-manager/pipelines/create').then(m => ({ default: m.NewPipeline })),
);
const PipelineDetail = React.lazy(() =>
  import('app/views/data-manager/pipelines/detail').then(m => ({ default: m.PipelineDetail })),
);
const EditPipeline = React.lazy(() =>
  import('app/views/data-manager/pipelines/edit').then(m => ({ default: m.EditPipeline })),
);
const PipelineRunDetail = React.lazy(() =>
  import('app/views/data-manager/pipelines/runs').then(m => ({ default: m.PipelineRunDetail })),
);

// Datasources
const Datasources = React.lazy(() =>
  import('app/views/data-manager/datasources').then(m => ({ default: m.Datasources })),
);
const NewDatasource = React.lazy(() =>
  import('app/views/data-manager/datasources/create').then(m => ({ default: m.NewDatasource })),
);
const ConfigureDatasource = React.lazy(() =>
  import('app/views/data-manager/datasources/create/configure').then(m => ({ default: m.ConfigureDatasource })),
);
const ReviewDatasource = React.lazy(() =>
  import('app/views/data-manager/datasources/create/review').then(m => ({ default: m.ReviewDatasource })),
);
const DatasourceDetail = React.lazy(() =>
  import('app/views/data-manager/datasources/detail').then(m => ({ default: m.DatasourceDetail })),
);
const EditDatasource = React.lazy(() =>
  import('app/views/data-manager/datasources/edit').then(m => ({ default: m.EditDatasource })),
);

// Chatbots — Chat Profiles (data_manager-backed)
const ChatProfiles = React.lazy(() =>
  import('app/views/chatbots/chat-profiles').then(m => ({ default: m.ChatProfiles })),
);
const NewChatProfile = React.lazy(() =>
  import('app/views/chatbots/chat-profiles/new').then(m => ({ default: m.NewChatProfile })),
);
const EditChatProfile = React.lazy(() =>
  import('app/views/chatbots/chat-profiles/edit').then(m => ({ default: m.EditChatProfile })),
);
const ChatProfileDetail = React.lazy(() =>
  import('app/views/chatbots/chat-profiles/detail').then(m => ({ default: m.ChatProfileDetail })),
);
const Preview = React.lazy(() => import('app/views/chatbots/preview').then(m => ({ default: m.Preview })));
const TranscriptionPage = React.lazy(() =>
  import('app/views/chatterton-page/transcription/new-transcription').then(m => ({ default: m.TranscriptionPage })),
);
const TranslationPage = React.lazy(() =>
  import('app/views/chatterton-page/translation').then(m => ({ default: m.TranslationPage })),
);

// Appels d'Offres
const WatchCriteriaPage = React.lazy(() =>
  import('app/views/tenders/settings/watch-criteria').then(m => ({ default: m.WatchCriteriaPage })),
);
const OrganizationPage = React.lazy(() =>
  import('app/views/tenders/settings/organization').then(m => ({ default: m.OrganizationPage })),
);
const AgenciesPage = React.lazy(() =>
  import('app/views/tenders/settings/agencies').then(m => ({ default: m.AgenciesPage })),
);
const OpportunitiesPage = React.lazy(() =>
  import('app/views/tenders/opportunities').then(m => ({ default: m.OpportunitiesPage })),
);
const ImportAOPage = React.lazy(() =>
  import('app/views/tenders/opportunities/import').then(m => ({ default: m.ImportAOPage })),
);
const OpportunityDetailPage = React.lazy(() =>
  import('app/views/tenders/opportunities/detail').then(m => ({ default: m.OpportunityDetailPage })),
);
const ActionsPage = React.lazy(() => import('app/views/tenders/actions').then(m => ({ default: m.ActionsPage })));
const PlatformAccountsPage = React.lazy(() =>
  import('app/views/tenders/settings/platform-accounts').then(m => ({ default: m.PlatformAccountsPage })),
);

// Lead Generation
const BookmarksPage = React.lazy(() => import('app/views/leadgen/bookmarks-page'));
const CompanyPage = React.lazy(() => import('app/views/leadgen/company-page').then(m => ({ default: m.CompanyPage })));
const DetailsPage = React.lazy(() => import('app/views/leadgen/details-page').then(m => ({ default: m.DetailsPage })));

/** Wrap an element in a feature-scoped ErrorBoundary. */
const withBoundary = (element: React.ReactElement, feature: string) => (
  <ErrorBoundary fallback={(reset: () => void) => <FeatureErrorFallback feature={feature} onRetry={reset} />}>
    {element}
  </ErrorBoundary>
);

/** Guard a create/edit route behind a permission (redirects to home if missing). */
const withPermission = (element: React.ReactElement, permission: string) => (
  <RequirePermission permission={permission}>{element}</RequirePermission>
);

export const MainRoutes = () => {
  return (
    <Suspense fallback={<LoadingSkeleton />}>
      <Routes>
        <Route index element={<LandingPage />} />

        <Route path={sitemap.login} element={<LoginRedirect />} />
        <Route path={sitemap.logout} element={<Logout />} />

        <Route path={sitemap.error} element={<ErrorPage />} />
        <Route path={sitemap.welcome} element={<Welcome />} />

        <Route path="/" element={<RequireAuth />}>
          <Route path={sitemap.home} element={<ModuleRoot />} />
          <Route path={patterns.home} element={<ModuleRoot />} />

          <Route path={sitemap.account} element={<AccountPage />} />
          <Route path={sitemap.user} element={<UserProfile />} />
          <Route path={sitemap.applications} element={<Applications />} />

          <Route path="invitations/:token/accept" element={<AcceptInvitation />} />

          {/* NewsLetters URLs */}
          <Route path={sitemap.newsletters.newsLetters} element={withBoundary(<NewsLettersPage />, 'Newsletters')} />
          <Route path={sitemap.newsletters.instances} element={withBoundary(<NewsLetterInstances />, 'Newsletters')} />
          <Route
            path={sitemap.newsletters.editInbound}
            element={withPermission(withBoundary(<EditInboundNewsLetter />, 'Newsletters'), 'newsletter:update')}
          />
          <Route
            path={sitemap.newsletters.editOutbound}
            element={withPermission(withBoundary(<EditOutboundNewsLetter />, 'Newsletters'), 'newsletter:update')}
          />
          <Route
            path={sitemap.newsletters.newInboundNewsLetter}
            element={withPermission(withBoundary(<NewInboundNewsLetter />, 'Newsletters'), 'newsletter:create')}
          />
          <Route
            path={sitemap.newsletters.newOutboundNewsLetter}
            element={withPermission(withBoundary(<NewOutboundNewsLetter />, 'Newsletters'), 'newsletter:create')}
          />
          <Route
            path={sitemap.newsletters.validateBlocks}
            element={withPermission(withBoundary(<ValidateBlocksPage />, 'Newsletters'), 'newsletter:update')}
          />
          <Route
            path={sitemap.newsletters.editHtml}
            element={withPermission(
              <React.Suspense fallback={null}>
                <NewsletterVisualEditor />
              </React.Suspense>,
              'newsletter:update',
            )}
          />

          {/* Data Manager URLs */}
          <Route path={sitemap.dataManager.sources} element={withBoundary(<Sources />, 'Data Manager')} />
          <Route path={sitemap.dataManager.source} element={withBoundary(<SourceManagement />, 'Data Manager')} />
          <Route path={sitemap.dataManager.collections} element={withBoundary(<Collections />, 'Data Manager')} />
          <Route
            path={sitemap.dataManager.newCollection}
            element={withPermission(withBoundary(<NewCollection />, 'Data Manager'), 'collection:create')}
          />
          <Route
            path={sitemap.dataManager.collectionDetail}
            element={withBoundary(<CollectionDetail />, 'Data Manager')}
          />
          <Route
            path={sitemap.dataManager.editCollection}
            element={withPermission(withBoundary(<EditCollection />, 'Data Manager'), 'collection:update')}
          />
          <Route path={sitemap.dataManager.pipelines} element={withBoundary(<Pipelines />, 'Data Manager')} />
          <Route path={sitemap.dataManager.collection} element={withBoundary(<Pipelines />, 'Data Manager')} />
          <Route
            path={sitemap.dataManager.newPipeline}
            element={withPermission(withBoundary(<NewPipeline />, 'Data Manager'), 'pipeline:create')}
          />
          <Route path={sitemap.dataManager.pipelineDetail} element={withBoundary(<PipelineDetail />, 'Data Manager')} />
          <Route
            path={sitemap.dataManager.editPipeline}
            element={withPermission(withBoundary(<EditPipeline />, 'Data Manager'), 'pipeline:update')}
          />
          <Route
            path={sitemap.dataManager.pipelineRunDetail}
            element={withBoundary(<PipelineRunDetail />, 'Data Manager')}
          />

          {/* Datasources URLs */}
          <Route path={sitemap.dataManager.datasources} element={withBoundary(<Datasources />, 'Data Manager')} />
          <Route
            path={sitemap.dataManager.newDatasource}
            element={withPermission(withBoundary(<NewDatasource />, 'Data Manager'), 'datasource:create')}
          />
          <Route
            path={sitemap.dataManager.configureDatasource}
            element={withPermission(withBoundary(<ConfigureDatasource />, 'Data Manager'), 'datasource:create')}
          />
          <Route
            path={sitemap.dataManager.reviewDatasource}
            element={withPermission(withBoundary(<ReviewDatasource />, 'Data Manager'), 'datasource:create')}
          />
          <Route
            path={sitemap.dataManager.datasourceDetail}
            element={withBoundary(<DatasourceDetail />, 'Data Manager')}
          />
          <Route
            path={sitemap.dataManager.editDatasource}
            element={withPermission(withBoundary(<EditDatasource />, 'Data Manager'), 'datasource:update')}
          />

          {/* Chatbots URLs */}
          <Route path={sitemap.chatbots['chat-profiles']} element={withBoundary(<ChatProfiles />, 'Chatbots')} />
          <Route
            path={sitemap.chatbots.newChatProfile}
            element={withPermission(withBoundary(<NewChatProfile />, 'Chatbots'), 'chat_profile:create')}
          />
          <Route
            path={sitemap.chatbots.editChatProfile}
            element={withPermission(withBoundary(<EditChatProfile />, 'Chatbots'), 'chat_profile:update')}
          />
          <Route path={sitemap.chatbots.chatProfileDetail} element={withBoundary(<ChatProfileDetail />, 'Chatbots')} />
          <Route path={sitemap.chatbots.preview} element={withBoundary(<Preview />, 'Chatbots')} />
          <Route path={sitemap.speak2me.transcription} element={withBoundary(<TranscriptionPage />, 'Speak2Me')} />
          <Route path={sitemap.speak2me.translation} element={withBoundary(<TranslationPage />, 'Speak2Me')} />

          {/* Appels d'Offres. TODO: wrap in `withPermission` once `company_profile:*` exists. */}
          <Route path={sitemap.tenders.settings} element={<Navigate to={sitemap.tenders.watchCriteria} replace />} />
          <Route
            path={sitemap.tenders.watchCriteria}
            element={withBoundary(<WatchCriteriaPage />, "Appels d'Offres")}
          />
          <Route path={sitemap.tenders.organization} element={withBoundary(<OrganizationPage />, "Appels d'Offres")} />
          <Route path={sitemap.tenders.agencies} element={withBoundary(<AgenciesPage />, "Appels d'Offres")} />
          <Route
            path={sitemap.tenders.platformAccounts}
            element={withBoundary(<PlatformAccountsPage />, "Appels d'Offres")}
          />
          <Route
            path={sitemap.tenders.opportunities}
            element={withBoundary(<OpportunitiesPage />, "Appels d'Offres")}
          />
          <Route path={sitemap.tenders.importAO} element={withBoundary(<ImportAOPage />, "Appels d'Offres")} />
          <Route
            path={sitemap.tenders.opportunityDetail}
            element={withBoundary(<OpportunityDetailPage />, "Appels d'Offres")}
          />
          <Route path={sitemap.tenders.actions} element={withBoundary(<ActionsPage />, "Appels d'Offres")} />

          {/* Lead Generation URLs */}
          <Route path={sitemap.leadgeneration.company} element={withBoundary(<CompanyPage />, 'Lead Generation')} />
          <Route path={sitemap.leadgeneration.details} element={withBoundary(<DetailsPage />, 'Lead Generation')} />
          <Route path={sitemap.leadgeneration.bookmarks} element={withBoundary(<BookmarksPage />, 'Lead Generation')} />
        </Route>

        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </Suspense>
  );
};
